import { TableBody, TableCell, TableHead, TableHeader, TableRow } from "./ui/table";
import { Button } from "./ui/button";
import { ShoppingCart } from "lucide-react";
import { generateYearRange, getWeeks, ucFirst, getStatusColor } from "@/utils/";
import { useState, useEffect } from "react";
import { CartDialog } from "./CartDialog";
import { useCalendar } from "@/contexts/CalendarContext";
import { Booking, Contract, CartItem, UnitAvailability, AvailabilityStatusText } from "@/types";
import { toast } from "sonner";

export interface CalendarMatrixProps {
  roomNumber: string;
  unitAvailability: UnitAvailability[];
  bookings: Booking[];
  contracts: Contract[];
  onBookWeeks: (contractId: number, bookingsToAdd: any[], bookingsResponse?: any) => void;
}

export function CalendarMatrix({ roomNumber, unitAvailability, bookings, contracts, onBookWeeks }: CalendarMatrixProps) {
  const { cartItems, addToCart, removeFromCart, clearCart } = useCalendar();
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [localUnitAvailability, setLocalUnitAvailability] = useState<UnitAvailability[]>([]);

  const years = generateYearRange();
  const weeks = getWeeks();

  useEffect(() => {
    setLocalUnitAvailability(unitAvailability);
  }, [unitAvailability]);

  const isWeekBooked = (week: number, year: number) => {
    return bookings.some(booking =>
      booking.unitNumber === roomNumber &&
      booking.week === week &&
      booking.years.includes(year) &&
      (booking.status === AvailabilityStatusText.Reserved || booking.status === AvailabilityStatusText.Booked)
    );
  };

  const handleRowClick = (week: number) => {
    const cartItemsForWeek = cartItems.filter(item => item.roomNumber === roomNumber && item.week === week);
    const yearsInCart = cartItemsForWeek.flatMap(item => item.years);

    const availableYears = years.filter(year => {
      const isBooked = isWeekBooked(week, year);
      const isAvailable = localUnitAvailability.some(a =>
        a.number === roomNumber &&
        a.week === week &&
        a.year === year &&
        a.status === AvailabilityStatusText.Available
      );
      return !isBooked && isAvailable;
    });

    if (availableYears.every(year => yearsInCart.includes(year))) {
      availableYears.forEach(year => removeFromCart(roomNumber, week, year));
      toast.success(`Removed all years for Week ${week}`);
    } else {
      const availRec = localUnitAvailability.find(a => a.number === roomNumber && a.week === week);
      addToCart({
        roomNumber,
        unitNumber: roomNumber,
        resort: availRec?.resort ?? '',
        week,
        years: availableYears,
        externalAvailabilityId: availRec?.id,
      });
      toast.success(`Added ${availableYears.length} years for Week ${week}`);
    }
  };

  const handleYearClick = (year: number, week: number) => {
    if (isWeekBooked(week, year)) return;

    const existingItem = cartItems.find(item =>
      item.roomNumber === roomNumber &&
      item.week === week &&
      item.years.includes(year)
    );

    if (existingItem) {
      removeFromCart(roomNumber, week, year);
    } else {
      const availRec = localUnitAvailability.find(a => a.number === roomNumber && a.week === week && a.year === year);
      addToCart({
        roomNumber,
        unitNumber: roomNumber,
        resort: availRec?.resort ?? '',
        week,
        years: [year],
        externalAvailabilityId: availRec?.id,
      });
    }
  };

  const handleConfirmBookings = async (contractId: number, bookingsResponse?: any) => {
    const bookingsToAdd = cartItems.map(item => ({
      _id: Date.now() + Math.random(),
      contractId,
      unitNumber: item.unitNumber || item.roomNumber,
      resort: item.resort,
      week: Number(item.week),
      years: item.years,
      status: AvailabilityStatusText.Reserved,
      externalAvailabilityId: item.externalAvailabilityId,
    }));

    if (cartItems.length > 0) {
      const updatedAvailability = [...localUnitAvailability];

      for (const item of cartItems) {
        for (const year of item.years) {
          const availIndex = updatedAvailability.findIndex(a =>
            a.number === roomNumber && a.week === item.week && a.year === year
          );
          if (availIndex !== -1) {
            updatedAvailability[availIndex] = {
              ...updatedAvailability[availIndex],
              status: AvailabilityStatusText.Reserved,
            };
          }
        }
      }

      setLocalUnitAvailability(updatedAvailability);
    }

    onBookWeeks(contractId, bookingsToAdd, bookingsResponse);
    clearCart();
    setIsCartOpen(false);
  };

  const getCellStatus = (year: number, week: number) => {
    const bookedBooking = bookings.find(booking =>
      booking.unitNumber === roomNumber &&
      booking.week === week &&
      booking.years.includes(year) &&
      (booking.status === AvailabilityStatusText.Reserved || booking.status === AvailabilityStatusText.Booked)
    );

    if (bookedBooking) {
      return {
        status: bookedBooking.status,
        contractId: bookedBooking.contractId,
        isFromWeeksApi: false,
      };
    }

    const cartItem = cartItems.find(item =>
      item.roomNumber === roomNumber &&
      item.week === week &&
      item.years.includes(year)
    );

    if (cartItem) {
      return {
        status: AvailabilityStatusText.Selected,
        contractId: null,
        isFromWeeksApi: false,
      };
    }

    const availability = localUnitAvailability.find(
      a => a.number === roomNumber && a.week === week && a.year === year
    );

    if (availability) {
      return {
        status: availability.status,
        contractId: 'system',
        isFromWeeksApi: true,
      };
    }

    return null; // no record → default Reserved in render
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-semibold"></h2>
        {cartItems.length > 0 && (
          <Button onClick={() => setIsCartOpen(true)} variant="default">
            <ShoppingCart className="w-4 h-4 mr-2" />
            Booking ({cartItems.length})
          </Button>
        )}
      </div>

      <div className="h-[600px] overflow-auto rounded-md border">
          <table className="w-full caption-bottom text-sm">
            <TableHeader className="sticky top-0 bg-background z-10">
              <TableRow>
                <TableHead className="sticky left-0 bg-background">Week</TableHead>
                {years.map((year) => (
                  <TableHead key={year} className="text-center min-w-[100px]">{year}</TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {weeks.map((week) => (
                <TableRow key={week}>
                  <TableCell
                    className="sticky left-0 bg-background font-medium cursor-pointer hover:bg-accent/80"
                    onClick={() => handleRowClick(week)}
                  >
                    {week}
                  </TableCell>
                  {years.map((year) => {
                    const status = getCellStatus(year, week);
                    return (
                      <TableCell key={`${year}-${week}`} className="text-center p-0">
                        {status ? (
                          <Button
                            variant="ghost"
                            className={`${getStatusColor(status.status, status.isFromWeeksApi)} w-full h-full rounded-none`}
                            onClick={() => handleYearClick(year, week)}
                            disabled={
                              status.status === AvailabilityStatusText.Reserved ||
                              status.status === AvailabilityStatusText.Booked
                            }
                          >
                            {ucFirst(status.status)}
                          </Button>
                        ) : (
                          <Button
                            variant="ghost"
                            className="bg-gray-100 text-gray-400 cursor-not-allowed w-full h-full rounded-none"
                            disabled
                          >
                            Unavailable
                          </Button>
                        )}
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))}
            </TableBody>
          </table>
      </div>

      <CartDialog
        isOpen={isCartOpen}
        onClose={() => setIsCartOpen(false)}
        cart={cartItems}
        contracts={contracts}
        onConfirm={handleConfirmBookings}
      />
    </div>
  );
}
